'use client';

import { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Divider,
  useMediaQuery,
  useTheme,
  alpha,
  Typography,
  Paper,
  Stack,
} from '@mui/material';
import { motion } from 'framer-motion';
import { LayoutContainer } from '../../kit';
import SupportHeader from './components/SupportHeader';
import SearchBar from './components/SearchBar';
import ContactSection from './components/ContactSection';
import FAQSection from './components/FAQSection';
import NeedHelpSection from './components/NeedHelpSection';
import { getFaqData } from './data/faqData';
import { Headset, QuestionAnswer } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import SimpleNav from '../../components/SimpleNav';
import { brandingConfig, getPhoneLink, getEmailLink } from '@/config/branding';
import { useTranslation } from '../../kit/useTranslation';

const MotionBox = motion.create(Box);
const MotionPaper = motion.create(Paper);

export default function SupportPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { t } = useTranslation();
  const faqData = getFaqData(t);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleContact = (type) => {
    switch (type) {
      case 'phone':
        window.location.href = getPhoneLink();
        break;
      case 'email':
        window.location.href = getEmailLink();
        break;
      case 'whatsapp':
        window.open(brandingConfig.contact.whatsappUrl, '_blank');
        break;
      case 'chat':
        // Chat functionality would go here
        break;
      case 'pilot':
        window.open(brandingConfig.messages.pilotPortalUrl, '_blank');
        break;
      default:
        break;
    }
  };
  const isAuthenticated = useSelector((state) => state.auth?.isLoggedIn);

  return (
    <LayoutContainer>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f8fafc',
          pb: 10,
          pt: isAuthenticated ? 4 : 8,
          pb: 4,
        }}
      >
        {!isAuthenticated && <SimpleNav />}
        <SupportHeader />

        <Container maxWidth="xl" sx={{ mt: -3, position: 'relative', zIndex: 2 }}>
          {/* Search Bar */}
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            sx={{ mb: 4 }}
          >
            <SearchBar value={searchQuery} onChange={setSearchQuery} />
          </MotionBox>

          {isDesktop ? (
            // Desktop Layout: Full width utilization with better grid
            <Grid container spacing={3}>
              {/* Contact Options - Full width grid on desktop */}
              <Grid item xs={12}>
                <MotionPaper
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      py: 2,
                      px: 2.5,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.2),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Headset sx={{ color: '#fff', fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: '#fff',
                            fontSize: '1rem',
                          }}
                        >
                          Contact Us
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: alpha('#fff', 0.8),
                          }}
                        >
                          {t('support.hereToHelp')}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                  <Box sx={{ p: 2.5 }}>
                    {/* Grid layout for contact cards */}
                    <Grid container spacing={2}>
                      {[
                        {
                          icon: '📞',
                          title: t('support.phoneSupport'),
                          subtitle: t('support.available247'),
                          color: '#4caf50',
                          type: 'phone',
                        },
                        {
                          icon: '💬',
                          title: t('support.whatsapp'),
                          subtitle: t('support.quickResponse'),
                          color: '#25D366',
                          type: 'whatsapp',
                        },
                        {
                          icon: '✉️',
                          title: t('support.email'),
                          subtitle: brandingConfig.contact.supportEmail,
                          color: '#2196f3',
                          type: 'email',
                        },
                        {
                          icon: '🎧',
                          title: t('support.liveChat'),
                          subtitle: t('support.chatWithOurTeam'),
                          color: '#9c27b0',
                          type: 'chat',
                        },
                        {
                          icon: '🚛',
                          title: 'Pilot Portal',
                          subtitle: 'Join our rider network',
                          color: '#FF9800', // Kinetic Amber
                          type: 'pilot',
                        },
                      ].map((option, idx) => (
                        <Grid item xs={6} md={2.4} key={idx}>
                          <Paper
                            onClick={() => handleContact(option.type)}
                            elevation={0}
                            sx={{
                              p: 2.5,
                              borderRadius: 2,
                              border: '1px solid #e2e8f0',
                              cursor: 'pointer',
                              textAlign: 'center',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: option.color,
                                bgcolor: alpha(option.color, 0.05),
                                transform: 'translateY(-2px)',
                                boxShadow: `0 4px 12px ${alpha(option.color, 0.2)}`,
                              },
                            }}
                          >
                            <Typography sx={{ fontSize: '2rem', mb: 1 }}>{option.icon}</Typography>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                color: '#1f2937',
                              }}
                            >
                              {option.title}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                mt: 0.5,
                              }}
                            >
                              {option.subtitle}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </MotionPaper>
              </Grid>

              {/* Quick Help Card - Below contact cards */}
              <Grid item xs={12}>
                <MotionPaper
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid #e2e8f0',
                    p: 2.5,
                  }}
                >
                  <NeedHelpSection onContact={handleContact} isDesktop />
                </MotionPaper>
              </Grid>

              {/* FAQs - Full width */}
              <Grid item xs={12}>
                <MotionPaper
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      py: 2,
                      px: 2.5,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.2),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <QuestionAnswer sx={{ color: '#fff', fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: '#fff',
                            fontSize: '1rem',
                          }}
                        >
                          Frequently Asked Questions
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: alpha('#fff', 0.8),
                          }}
                        >
                          {t('support.faqSubtitle')}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                  <Box sx={{ p: 2.5 }}>
                    <FAQSection
                      faqData={faqData}
                      searchQuery={searchQuery}
                      expanded={expanded}
                      onAccordionChange={handleAccordionChange}
                      isDesktop
                    />
                  </Box>
                </MotionPaper>
              </Grid>
            </Grid>
          ) : (
            // Mobile Layout: Single column
            <>
              <ContactSection onContact={handleContact} />
              <Divider sx={{ my: 3 }} />
              <FAQSection
                faqData={faqData}
                searchQuery={searchQuery}
                expanded={expanded}
                onAccordionChange={handleAccordionChange}
              />
              <NeedHelpSection onContact={handleContact} />
            </>
          )}
        </Container>
      </Box>
    </LayoutContainer>
  );
}
